import { supabase } from '../../lib/supabase'
import { getCurrentUserId } from '../../lib/auth'
import type { TenantDocumentType } from '../../types/tenancy'

// ============================================================
// DOCUMENTS TO SERVE COUNT (for sidebar badge)
// ============================================================

const REQUIRED_DOCUMENT_TYPES: TenantDocumentType[] = [
  'gas_safety',
  'eicr',
  'epc',
  'deposit_cert'
]

// ============================================================
// PROPERTY OCCUPANCY STATUS
// ============================================================

export type OccupancyStatus = 'vacant' | 'occupied' | 'ending_soon' | 'moving_in'

export interface PropertyOccupancyMap {
  [propertyId: string]: {
    status: OccupancyStatus
    startDate?: string
    endDate?: string
    totalUnits?: number
    occupiedUnits?: number
  }
}

export async function getPropertyOccupancyStatus(): Promise<PropertyOccupancyMap> {
  const userId = await getCurrentUserId()

  // Get tenancies and unit counts in parallel
  const [tenancyResult, unitResult] = await Promise.all([
    supabase
      .from('tenancies')
      .select('property_id, unit_id, status, start_date, end_date, property:properties!inner(owner_id)')
      .eq('property.owner_id', userId)
      .in('status', ['onboarding', 'moving_in', 'active', 'ending']),
    supabase
      .from('units')
      .select('id, property_id, unit_type')
      .eq('owner_id', userId)
      .eq('is_active', true),
  ])

  if (tenancyResult.error) throw tenancyResult.error
  if (unitResult.error) throw unitResult.error

  const tenancies = tenancyResult.data
  const units = unitResult.data || []

  // Count active units per property (exclude whole_property for multi-unit)
  const unitCountsByProperty: Record<string, number> = {}
  const propertyHasRealUnits: Record<string, boolean> = {}
  for (const unit of units) {
    if (unit.unit_type !== 'whole_property') {
      propertyHasRealUnits[unit.property_id] = true
      unitCountsByProperty[unit.property_id] = (unitCountsByProperty[unit.property_id] || 0) + 1
    }
  }

  const occupancyMap: PropertyOccupancyMap = {}

  // Calculate "ending soon" threshold (30 days from now)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const endingSoonThreshold = new Date()
  endingSoonThreshold.setDate(endingSoonThreshold.getDate() + 30)

  // Track occupied unit count per property (for multi-unit display)
  const occupiedUnitsByProperty: Record<string, Set<string>> = {}

  for (const tenancy of tenancies || []) {
    const propertyId = tenancy.property_id
    const startDate = tenancy.start_date ? new Date(tenancy.start_date) : null
    const endDate = tenancy.end_date ? new Date(tenancy.end_date) : null

    // Determine status based on tenancy state
    let tenancyStatus: OccupancyStatus

    if (startDate && startDate > today) {
      tenancyStatus = 'moving_in'
    } else if (tenancy.status === 'ending') {
      tenancyStatus = 'ending_soon'
    } else if (endDate && endDate <= endingSoonThreshold) {
      tenancyStatus = 'ending_soon'
    } else if (tenancy.status === 'active') {
      tenancyStatus = 'occupied'
    } else {
      tenancyStatus = 'moving_in'
    }

    // Track which units are occupied for multi-unit properties
    if (tenancy.unit_id && propertyHasRealUnits[propertyId]) {
      if (!occupiedUnitsByProperty[propertyId]) occupiedUnitsByProperty[propertyId] = new Set()
      occupiedUnitsByProperty[propertyId].add(tenancy.unit_id)
    }

    const currentStatus = occupancyMap[propertyId]?.status

    // Priority: occupied > ending_soon > moving_in
    if (!currentStatus || tenancyStatus === 'occupied') {
      occupancyMap[propertyId] = {
        status: tenancyStatus,
        startDate: tenancy.start_date || undefined,
        endDate: tenancy.end_date || undefined,
      }
    }
  }

  // Attach unit counts for multi-unit properties
  for (const propertyId of Object.keys(unitCountsByProperty)) {
    const total = unitCountsByProperty[propertyId]
    const occupied = occupiedUnitsByProperty[propertyId]?.size || 0
    if (occupancyMap[propertyId]) {
      occupancyMap[propertyId].totalUnits = total
      occupancyMap[propertyId].occupiedUnits = occupied
    } else {
      // All units vacant — property not in map yet
      occupancyMap[propertyId] = { status: 'vacant', totalUnits: total, occupiedUnits: 0 }
    }
  }

  return occupancyMap
}

export async function getActiveTenancyCountsByProperty(): Promise<Record<string, number>> {
  const userId = await getCurrentUserId()

  const { data: tenancies, error } = await supabase
    .from('tenancies')
    .select('property_id, property:properties!inner(owner_id)')
    .eq('property.owner_id', userId)
    .in('status', ['onboarding', 'moving_in', 'active', 'ending'])

  if (error) throw error

  const counts: Record<string, number> = {}
  for (const t of tenancies || []) {
    counts[t.property_id] = (counts[t.property_id] || 0) + 1
  }
  return counts
}

export async function getDocumentsToServeCount(): Promise<number> {
  const userId = await getCurrentUserId()

  // Get active tenancies for properties owned by the current user
  const { data: tenancies, error: tenanciesError } = await supabase
    .from('tenancies')
    .select('id, property:properties!inner(owner_id)')
    .eq('property.owner_id', userId)
    .in('status', ['active', 'moving_in'])

  if (tenanciesError) throw tenanciesError
  if (!tenancies || tenancies.length === 0) return 0

  const tenancyIds = tenancies.map(t => t.id)

  // Get all served documents for active tenancies
  const { data: documents, error: documentsError } = await supabase
    .from('tenant_documents')
    .select('tenancy_id, document_type')
    .in('tenancy_id', tenancyIds)

  if (documentsError) throw documentsError

  // Count tenancies that have at least one missing required document
  let tenanciesNeedingDocs = 0
  for (const tenancy of tenancies) {
    const tenancyDocs = documents?.filter(d => d.tenancy_id === tenancy.id) || []
    const servedTypes = tenancyDocs.map(d => d.document_type)

    const hasMissingDoc = REQUIRED_DOCUMENT_TYPES.some(
      requiredType => !servedTypes.includes(requiredType)
    )
    if (hasMissingDoc) {
      tenanciesNeedingDocs++
    }
  }

  return tenanciesNeedingDocs
}
